// api/services/assistants.service.ts
import { api } from '../client';
import { Assistant } from '@/types';
import {Brain} from "lucide-react";
import {ApiResponse} from "@/api";

export interface ApiAssistant {
    id: string;
    code: string;
    name: string;
    description: string;
    category?: string;
    systemPrompt: string;
    status: string;
    active: boolean;
    iconUrl?: string;
    colorScheme?: string;
    bgGradient?: string;
    suggestedPrompts?: string[];
    assignedModules?: any[];
    deployedVersion?: number;
    deployedAt?: string;
    createdAt: string;
    updatedAt: string;
    totalRequests?: number;
    successfulRequests?: number;
    avgResponseTimeMs?: number;
    versionNotes?: string;
    isCurrentVersion?: boolean;
    version?: number;
}

export const assistantsService = {
    // Fetch all published assistants
    getPublishedAssistants: async (): Promise<ApiAssistant[]> => {
        try {
            const response = await api.get<ApiResponse<ApiAssistant[]>>('/api/ai/assistants/published');

            if (response.data?.success && response.data?.data) {
                return response.data.data;
            }

            console.error('Invalid response format:', response.data);
            return [];
        } catch (error) {
            console.error('Failed to fetch assistants:', error);
            return [];
        }
    },

    // Fetch a single assistant by code or name (filters from published assistants)
    getAssistantByCode: async (code: string): Promise<ApiAssistant | null> => {
        try {
            const assistants = await assistantsService.getPublishedAssistants();
            // Try to find by code first, then fall back to name
            return assistants.find(a => a.code === code || a.name === code) || null;
        } catch (error) {
            console.error('Failed to fetch assistant by code:', error);
            return null;
        }
    },

    // Execute assistant (for chat)
    executeAssistant: async (code: string, message: string, context?: any): Promise<string> => {
        try {
            const response = await api.post<{ response: string }>(
                `/api/ai/assistants/execute/${code}`,
                { message, context }
            );
            return response.data.response;
        } catch (error) {
            console.error('Failed to execute assistant:', error);
            throw error;
        }
    }
};

// Helper to convert API assistant to your app's Assistant type
export const convertApiAssistant = (apiAssistant: ApiAssistant): Assistant => {
    // Determine color scheme
    const colorScheme = apiAssistant.colorScheme || 'blue';

    // Map color schemes to gradients and styling
    const colorMappings: Record<string, {
        bgGradient: string;
        lightBg: string;
        borderColor: string;
        accentColor: string;
    }> = {
        blue: {
            bgGradient: 'from-blue-500 to-blue-600',
            lightBg: 'bg-blue-50',
            borderColor: 'border-blue-200',
            accentColor: 'text-blue-600'
        },
        green: {
            bgGradient: 'from-green-500 to-green-600',
            lightBg: 'bg-green-50',
            borderColor: 'border-green-200',
            accentColor: 'text-green-600'
        },
        purple: {
            bgGradient: 'from-purple-500 to-purple-600',
            lightBg: 'bg-purple-50',
            borderColor: 'border-purple-200',
            accentColor: 'text-purple-600'
        },
        red: {
            bgGradient: 'from-red-500 to-red-600',
            lightBg: 'bg-red-50',
            borderColor: 'border-red-200',
            accentColor: 'text-red-600'
        },
        orange: {
            bgGradient: 'from-orange-500 to-orange-600',
            lightBg: 'bg-orange-50',
            borderColor: 'border-orange-200',
            accentColor: 'text-orange-600'
        },
        yellow: {
            bgGradient: 'from-yellow-500 to-yellow-600',
            lightBg: 'bg-yellow-50',
            borderColor: 'border-yellow-200',
            accentColor: 'text-yellow-600'
        },
        pink: {
            bgGradient: 'from-pink-500 to-pink-600',
            lightBg: 'bg-pink-50',
            borderColor: 'border-pink-200',
            accentColor: 'text-pink-600'
        },
        indigo: {
            bgGradient: 'from-indigo-500 to-indigo-600',
            lightBg: 'bg-indigo-50',
            borderColor: 'border-indigo-200',
            accentColor: 'text-indigo-600'
        }
    };

    const colorConfig = colorMappings[colorScheme] || colorMappings.blue;

    // Use API bgGradient if provided, otherwise use mapped gradient
    const finalBgGradient = apiAssistant.bgGradient || colorConfig.bgGradient;

    // Default suggested prompts if none provided
    const defaultPrompts = [
        "How can I help you today?",
        "What would you like to know?",
        "Ask me anything!",
        "Let's get started!"
    ];

    return {
        id: apiAssistant.id,
        name: apiAssistant.name,
        code: apiAssistant.code || apiAssistant.name, // Store code or fallback to name for icon identification
        description: apiAssistant.description || 'AI Assistant',
        icon: apiAssistant.iconUrl ? {
            kind: 'image',
            src: apiAssistant.iconUrl
        } : {
            kind: 'component',
            component: Brain // Default to Brain icon
        },
        color: colorScheme,
        bgGradient: finalBgGradient,
        lightBg: colorConfig.lightBg,
        borderColor: colorConfig.borderColor,
        accentColor: colorConfig.accentColor,
        suggestedPrompts: apiAssistant.suggestedPrompts && apiAssistant.suggestedPrompts.length > 0
            ? apiAssistant.suggestedPrompts
            : defaultPrompts,
        systemPrompt: apiAssistant.systemPrompt,
        category: apiAssistant.category
    };
};
